import React, { useCallback, useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Steps,
  Card,
  Form,
  Input,
  Button,
  Typography,
  Row,
  Col,
  List,
  Divider,
  Space,
  message,
  Alert,
  Radio,
  Collapse,
  Select,
} from "antd";
import {
  UserOutlined,
  ShoppingCartOutlined,
  CreditCardOutlined,
  CheckCircleOutlined,
} from "@ant-design/icons";
import API_CONFIG from "../config/api.js";
import LocationPicker from "./components/LocationPicker";

const { Title, Text } = Typography;
const { Step } = Steps;
const { Panel } = Collapse;

const formatCurrency = (amount: number) =>
  new Intl.NumberFormat("fr-FR", {
    style: "currency",
    currency: "XOF",
  }).format(amount);

interface CartItem {
  id: number;
  product_name: string;
  quantity: number;
  unit_price: number;
  item_total: number;
}

interface DeliveryZone {
  id: number;
  name: string;
  delivery_fee: number;
}

interface DeliveryInfo {
  delivery_policy?: string | null;
  return_policy?: string | null;
  delivery_zones: DeliveryZone[];
  delivery_zones_configured?: boolean;
  delivery_available?: boolean;
  legacy_delivery_zones?: string | null;
  delivery_timeframe?: string | null;
  min_order_amount: number;
  delivery_cost_info?: string | null;
}

const Checkout: React.FC = () => {
  const { storeSlug } = useParams<{ storeSlug: string }>();
  const navigate = useNavigate();

  const [currentStep, setCurrentStep] = useState(0);
  const [fulfillments, setFulfillments] = useState<{ warehouse_name: string; items: { product_id: number; product_name: string; quantity: number }[] }[]>([]);
  const [cartItems, setCartItems] = useState<CartItem[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [customerForm] = Form.useForm();
  const [paymentMethod, setPaymentMethod] = useState("cod"); // cash on delivery
  const [orderPlaced, setOrderPlaced] = useState(false);
  const [orderNumber, setOrderNumber] = useState("");
  const [customerInfo, setCustomerInfo] = useState<any>(null); // Store customer info
  const [isMobile, setIsMobile] = useState(false);
  const [deliveryInfo, setDeliveryInfo] = useState<DeliveryInfo | null>(null);
  const [deliveryInfoLoading, setDeliveryInfoLoading] = useState(true);
  const [deliveryInfoError, setDeliveryInfoError] = useState(false);
  const [selectedDeliveryZoneId, setSelectedDeliveryZoneId] = useState<
    number | null
  >(null);
  const [shippingLocation, setShippingLocation] = useState<any>(null); // ✨ PHASE 2: Adresse structurée
  const [paymentMethodsAvailable, setPaymentMethodsAvailable] = useState<{
    cod: boolean;
    yengapay: boolean;
  }>({ cod: true, yengapay: false }); // 💳 Modes de paiement activés pour la boutique
  const [paymentMethodsLoading, setPaymentMethodsLoading] = useState(true);
  const [promotionInput, setPromotionInput] = useState('');
  const [promotionLoading, setPromotionLoading] = useState(false);
  const [appliedPromotion, setAppliedPromotion] = useState<{ code: string; discount_amount: number } | null>(null);
  const promotionRequestIdRef = useRef(0);

  const hasOnlinePayment = paymentMethodsAvailable.yengapay;
  const deliveryZones = deliveryInfo?.delivery_zones || [];
  const selectedDeliveryZone = deliveryZones.find(
    (zone) => zone.id === selectedDeliveryZoneId
  );
  const shippingAmount = selectedDeliveryZone?.delivery_fee || 0;
  const orderTotal = Math.round((total + shippingAmount - (appliedPromotion?.discount_amount || 0)) * 100) / 100;
  const deliveryUnavailable = deliveryInfo?.delivery_available === false;

  useEffect(() => {
    promotionRequestIdRef.current += 1;
    setAppliedPromotion(null);
    setPromotionLoading(false);
  }, [total, selectedDeliveryZoneId]);

  const handleShippingLocationSelect = useCallback(
    (location: any) => {
      setShippingLocation(location);
      customerForm.setFieldValue("shipping_location", location);
    },
    [customerForm]
  );

  // Hook pour détecter la taille d'écran
  useEffect(() => {
    const checkIsMobile = () => {
      setIsMobile(window.innerWidth < 768); // Mobile breakpoint
    };

    checkIsMobile();
    window.addEventListener("resize", checkIsMobile);
    return () => window.removeEventListener("resize", checkIsMobile);
  }, []);

  // URLs sécurisées pour APIs publiques uniquement
  const STORE_API = API_CONFIG.PUBLIC_STORE_API;

  useEffect(() => {
    fetchCart();
    fetchDeliveryInfo(); // ✨ PHASE 1: Charger les politiques de livraison
    fetchPaymentMethods(); // 💳 Charger les modes de paiement disponibles
  }, [storeSlug]);

  const fetchCart = async () => {
    try {
      const sessionId = localStorage.getItem("ecommerce_session_id");
      if (!sessionId) {
        navigate(`/store/${storeSlug}`);
        return;
      }

      const response = await fetch(`${STORE_API}/${storeSlug}/cart`, {
        headers: {
          "X-Session-ID": sessionId,
        },
      });

      if (response.ok) {
        const data = await response.json();
        if (!data.items || data.items.length === 0) {
          navigate(`/store/${storeSlug}`);
          return;
        }
        setCartItems(data.items);
        setFulfillments(data.fulfillments || []);
        setTotal(parseFloat(data.total));
      }
    } catch (error) {
      console.error("Error fetching cart:", error);
      navigate(`/store/${storeSlug}`);
    }
  };

  // ✨ PHASE 1: Récupérer les politiques de livraison
  const fetchDeliveryInfo = async () => {
    setDeliveryInfoLoading(true);
    setDeliveryInfoError(false);
    setDeliveryInfo(null);
    setSelectedDeliveryZoneId(null);
    try {
      const response = await fetch(`${STORE_API}/${storeSlug}/delivery-info`);
      if (!response.ok) {
        throw new Error(`Delivery info request failed (${response.status})`);
      }
      const data = await response.json();
      const zones: DeliveryZone[] = Array.isArray(data.delivery_zones)
        ? data.delivery_zones.map((zone: any) => ({
            id: Number(zone.id),
            name: String(zone.name || ""),
            delivery_fee: Number(zone.delivery_fee || 0),
          }))
        : [];
      setDeliveryInfo({
        ...data,
        delivery_zones: zones,
        min_order_amount: Number(data.min_order_amount || 0),
      });
    } catch (error) {
      console.error("Error fetching delivery info:", error);
      setDeliveryInfoError(true);
    } finally {
      setDeliveryInfoLoading(false);
    }
  };

  // 💳 Récupérer les modes de paiement disponibles pour la boutique
  const fetchPaymentMethods = async () => {
    setPaymentMethodsLoading(true);
    try {
      const response = await fetch(`${STORE_API}/${storeSlug}/payment-methods`);
      if (response.ok) {
        const data = await response.json();
        const availableMethods = {
          cod: data.cod !== false,
          yengapay: Boolean(data.yengapay),
        };

        setPaymentMethodsAvailable(availableMethods);
        if (availableMethods.yengapay && !availableMethods.cod) {
          setPaymentMethod("yengapay");
        }
      }
    } catch (error) {
      console.error("Error fetching payment methods:", error);
      // Repli silencieux : la commande reste possible sans paiement en ligne.
    } finally {
      setPaymentMethodsLoading(false);
    }
  };

  const applyPromotion = async () => {
    const sessionId = localStorage.getItem('ecommerce_session_id');
    if (!sessionId || !promotionInput.trim()) return;
    const requestId = ++promotionRequestIdRef.current;
    setPromotionLoading(true);
    try {
      const response = await fetch(`${STORE_API}/${storeSlug}/promotions/quote`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Session-ID': sessionId },
        body: JSON.stringify({
          promotion_code: promotionInput.trim(),
          delivery_zone_id: selectedDeliveryZoneId,
        }),
      });
      const data = await response.json().catch(() => ({}));
      if (requestId !== promotionRequestIdRef.current) return;
      if (!response.ok) throw new Error(data.error || 'Code promotionnel indisponible.');
      if (Math.abs(Number(data.subtotal_amount) - total) > 0.009) {
        await fetchCart();
        throw new Error('Le montant du panier a changé. Vérifiez le panier et réessayez.');
      }
      setAppliedPromotion({ code: data.code, discount_amount: Number(data.discount_amount) });
      setPromotionInput(data.code);
      message.success('Code promotionnel appliqué.');
    } catch (error) {
      if (requestId !== promotionRequestIdRef.current) return;
      setAppliedPromotion(null);
      message.error(error instanceof Error ? error.message : 'Code promotionnel indisponible.');
    } finally {
      if (requestId === promotionRequestIdRef.current) setPromotionLoading(false);
    }
  };

  const promotionControls = (
    <div style={{ marginTop: 12 }}>
      <Text strong>Code promotionnel</Text>
      <Space.Compact style={{ width: '100%', marginTop: 6 }}>
        <Input
          aria-label="Code promotionnel"
          value={promotionInput}
          maxLength={40}
          placeholder="Saisissez votre code"
          onChange={(event) => { promotionRequestIdRef.current += 1; setPromotionInput(event.target.value); setAppliedPromotion(null); setPromotionLoading(false); }}
        />
        <Button type="primary" loading={promotionLoading} disabled={!promotionInput.trim() || deliveryUnavailable || (deliveryZones.length > 0 && !selectedDeliveryZoneId)} onClick={() => void applyPromotion()}>
          Appliquer
        </Button>
      </Space.Compact>
      {appliedPromotion && <Text type="success">{appliedPromotion.code} appliqué · {formatCurrency(appliedPromotion.discount_amount)} de réduction</Text>}
    </div>
  );

  const placeOrder = async (
    orderCustomerInfo = customerInfo,
    selectedPaymentMethod: string | null = paymentMethod
  ) => {
    setLoading(true);
    try {
      if (!orderCustomerInfo) {
        message.error(
          "Informations client manquantes. Veuillez revenir à l'étape précédente."
        );
        setCurrentStep(0);
        setLoading(false);
        return;
      }

      // Validation côté client avant envoi
      if (
        !orderCustomerInfo.customer_name ||
        orderCustomerInfo.customer_name.trim() === ""
      ) {
        message.error("Le nom complet est requis");
        setCurrentStep(0);
        setLoading(false);
        return;
      }

      if (
        !orderCustomerInfo.customer_phone ||
        orderCustomerInfo.customer_phone.trim() === ""
      ) {
        message.error("Le téléphone est requis");
        setCurrentStep(0);
        setLoading(false);
        return;
      }

      // ✨ PHASE 2: Validation adresse structurée
      if (!orderCustomerInfo.shipping_location?.street_address?.trim()) {
        message.error("L'adresse de livraison est requise");
        setCurrentStep(0);
        setLoading(false);
        return;
      }

      if (
        deliveryZones.length > 0 &&
        !Number.isInteger(Number(orderCustomerInfo.delivery_zone_id))
      ) {
        message.error("Veuillez sélectionner une zone de livraison");
        setCurrentStep(0);
        setLoading(false);
        return;
      }
      if (deliveryUnavailable) {
        message.error(
          "Cette boutique ne propose actuellement aucune zone de livraison"
        );
        setCurrentStep(0);
        setLoading(false);
        return;
      }

      const sessionId = localStorage.getItem("ecommerce_session_id");

      if (!sessionId) {
        message.error("Votre session a expiré. Veuillez rafraîchir la page.");
        setLoading(false);
        return;
      }

      // ✨ PHASE 2: Préparer les données avec adresse structurée
      const orderData = {
        customer_name: orderCustomerInfo.customer_name?.trim(),
        customer_email: orderCustomerInfo.customer_email?.trim() || null,
        customer_phone: orderCustomerInfo.customer_phone?.trim(),
        shipping_location: orderCustomerInfo.shipping_location, // Objet structuré
        delivery_zone_id: orderCustomerInfo.delivery_zone_id || null,
        payment_method: selectedPaymentMethod,
        expected_subtotal: total,
        promotion_code: appliedPromotion?.code || null,
      };

      // Debug: Log order data being sent

      const response = await fetch(`${STORE_API}/${storeSlug}/orders`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Session-ID": sessionId,
        },
        body: JSON.stringify(orderData),
      });

      if (response.ok) {
        const result = await response.json();

        // 💳 Paiement en ligne YengaPay : mémoriser la commande puis rediriger
        // vers la page de paiement hébergée. Le statut sera confirmé au retour
        // (page PaymentReturn) et par le webhook signé.
        if (result.payment_url) {
          try {
            localStorage.setItem("yengapay_pending_order", result.order_number);
            localStorage.setItem("yengapay_pending_store", storeSlug || "");
          } catch (_) {
            /* localStorage indisponible : la page de retour lira l'URL */
          }
          window.location.href = result.payment_url;
          return;
        }

        setOrderNumber(result.order_number);
        setOrderPlaced(true);
        setCurrentStep(hasOnlinePayment ? 2 : 1);

        // Clear cart
        localStorage.removeItem("ecommerce_session_id");

        message.success("Commande passée avec succès!");
      } else {
        const error = await response.json();
        message.error(error.error || "Erreur lors de la commande");
        if (error.code === 'ECOMMERCE_CART_PRICE_CHANGED' || error.code === 'ECOMMERCE_STOCK_INSUFFICIENT' || error.stock_issues?.length) {
          await fetchCart(); setCurrentStep(0);
        }
        if (String(error.code || "").startsWith("DELIVERY_")) {
          customerForm.setFieldValue("delivery_zone_id", undefined);
          setSelectedDeliveryZoneId(null);
          setCurrentStep(0);
          await fetchDeliveryInfo();
        }
        if (String(error.code || '').startsWith('PROMOTION_') || String(error.code || '').startsWith('ECOMMERCE_DISCOUNT_')) {
          setAppliedPromotion(null);
        }
      }
    } catch (error) {
      console.error("Error placing order:", error);
      message.error("Erreur de connexion");
    } finally {
      setLoading(false);
    }
  };

  const handleCustomerInfoSubmit = async (values: any) => {
    if (paymentMethodsLoading || deliveryInfoLoading || loading) return;
    if (deliveryInfoError) {
      message.error(
        "Impossible de vérifier les zones de livraison. Veuillez réessayer."
      );
      return;
    }

    setCustomerInfo(values);

    if (hasOnlinePayment) {
      setCurrentStep(1);
      return;
    }

    // Sans paiement en ligne, le règlement est différé et sera encaissé en interne.
    await placeOrder(values, "cod");
  };

  const steps = [
    {
      title: "Informations client",
      icon: <UserOutlined />,
    },
    ...(hasOnlinePayment
      ? [
          {
            title: "Mode de règlement",
            icon: <CreditCardOutlined />,
          },
        ]
      : []),
    {
      title: "Confirmation",
      icon: <CheckCircleOutlined />,
    },
  ];

  if (orderPlaced) {
    return (
      <div style={{ padding: "2rem", maxWidth: 800, margin: "0 auto" }}>
        <Card style={{ textAlign: "center" }}>
          <CheckCircleOutlined
            style={{ fontSize: 64, color: "#52c41a", marginBottom: "1rem" }}
          />
          <Title level={2}>Commande confirmée!</Title>
          <Text style={{ fontSize: 16 }}>
            Votre commande <strong>#{orderNumber}</strong> a été enregistrée
            avec succès.
          </Text>
          <Divider />
          <Space>
            <Button
              type="primary"
              onClick={() => navigate(`/store/${storeSlug}`)}
            >
              Continuer les achats
            </Button>
          </Space>
        </Card>
      </div>
    );
  }

  return (
    <div
      style={{
        padding: isMobile ? "1rem" : "2rem",
        maxWidth: 1200,
        margin: "0 auto",
      }}
    >
      <Title level={isMobile ? 3 : 2}>
        <ShoppingCartOutlined />{" "}
        {isMobile ? "Ma commande" : "Finaliser ma commande"}
      </Title>

      <Steps
        current={currentStep}
        style={{ marginBottom: "2rem" }}
        direction={isMobile ? "vertical" : "horizontal"}
        size={isMobile ? "small" : "default"}
      >
        {steps.map((step, index) => (
          <Step
            key={index}
            title={isMobile ? step.title : step.title}
            icon={step.icon}
          />
        ))}
      </Steps>
      {fulfillments.length > 1 && !orderPlaced && <Alert type="info" showIcon style={{ marginBottom: 20 }}
        message={`Préparations prévues dans ${fulfillments.length} établissements`}
        description={<div>
          <p>Un seul paiement et des frais de livraison uniques selon la zone choisie. Les préparations peuvent être livrées séparément, dans le délai annoncé par la boutique.</p>
          {fulfillments.map((group, index) => <p key={index}><strong>{group.warehouse_name}</strong> : {group.items.map((item) => `${item.product_name} × ${item.quantity}`).join(', ')}</p>)}
        </div>} />}


      <Row gutter={24}>
        {/* Récapitulatif au-dessus sur mobile */}
        {isMobile && (
          <Col span={24} style={{ marginBottom: "1rem" }}>
            <Card title="Récapitulatif de commande" size="small">
              <List
                dataSource={cartItems}
                renderItem={(item) => (
                  <List.Item style={{ padding: "0.5rem 0" }}>
                    <List.Item.Meta
                      title={
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            fontSize: 14,
                          }}
                        >
                          <Text>{item.product_name}</Text>
                          <Text>x{item.quantity}</Text>
                        </div>
                      }
                      description={
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                          }}
                        >
                          <Text type="secondary" style={{ fontSize: 12 }}>
                            {new Intl.NumberFormat("fr-FR", {
                              style: "currency",
                              currency: "XOF",
                            }).format(item.unit_price)}{" "}
                            / unité
                          </Text>
                          <Text strong style={{ fontSize: 12 }}>
                            {new Intl.NumberFormat("fr-FR", {
                              style: "currency",
                              currency: "XOF",
                            }).format(item.item_total)}
                          </Text>
                        </div>
                      }
                    />
                  </List.Item>
                )}
              />
              <Divider style={{ margin: "8px 0" }} />
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Text>Sous-total</Text>
                <Text>{formatCurrency(total)}</Text>
              </div>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginTop: 6,
                }}
              >
                <Text>
                  Livraison
                  {selectedDeliveryZone ? ` · ${selectedDeliveryZone.name}` : ""}
                </Text>
                <Text>
                  {deliveryInfoError || deliveryUnavailable
                    ? "Indisponible"
                    : deliveryZones.length > 0 && !selectedDeliveryZone
                    ? "À sélectionner"
                    : formatCurrency(shippingAmount)}
                </Text>
              </div>
              {promotionControls}
              {appliedPromotion && (
                <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 8 }}>
                  <Text>Remise {appliedPromotion.code}</Text>
                  <Text type="success">−{formatCurrency(appliedPromotion.discount_amount)}</Text>
                </div>
              )}
              <Divider style={{ margin: "8px 0" }} />
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Text strong>Total à payer</Text>
                <Text strong style={{ color: "#52c41a" }}>
                  {formatCurrency(orderTotal)}
                </Text>
              </div>
            </Card>
          </Col>
        )}

        <Col span={isMobile ? 24 : 16}>
          {currentStep === 0 && (
            <Card title="Informations de livraison">
              {/* ✨ PHASE 1: Affichage des politiques de livraison */}
              {deliveryInfo &&
                (deliveryInfo.delivery_policy ||
                  deliveryInfo.delivery_timeframe ||
                  deliveryInfo.delivery_cost_info ||
                  deliveryInfo.legacy_delivery_zones ||
                  deliveryZones.length > 0) && (
                  <Alert
                    message="Informations de livraison"
                    description={
                      <div style={{ marginTop: "0.5rem" }}>
                        {deliveryInfo.delivery_timeframe && (
                          <div style={{ marginBottom: "0.5rem" }}>
                            <Text strong>Délais : </Text>
                            <Text>{deliveryInfo.delivery_timeframe}</Text>
                          </div>
                        )}
                        {deliveryZones.length > 0 && (
                          <div style={{ marginBottom: "0.5rem" }}>
                            <Text strong>Zones desservies : </Text>
                            <Text>
                              {deliveryZones.length} zone
                              {deliveryZones.length > 1 ? "s" : ""} tarifée
                              {deliveryZones.length > 1 ? "s" : ""} disponible
                              {deliveryZones.length > 1 ? "s" : ""}
                            </Text>
                          </div>
                        )}
                        {deliveryZones.length === 0 &&
                          deliveryInfo.legacy_delivery_zones && (
                          <div style={{ marginBottom: "0.5rem" }}>
                            <Text strong>Zones annoncées : </Text>
                            <Text>{deliveryInfo.legacy_delivery_zones}</Text>
                          </div>
                        )}
                        {deliveryInfo.min_order_amount > 0 && (
                          <div style={{ marginBottom: "0.5rem" }}>
                            <Text strong>Commande minimum : </Text>
                            <Text>
                              {formatCurrency(deliveryInfo.min_order_amount)}
                            </Text>
                          </div>
                        )}
                        {deliveryInfo.delivery_cost_info && (
                          <div style={{ marginBottom: "0.5rem" }}>
                            <Text strong>Frais de livraison : </Text>
                            <Text>{deliveryInfo.delivery_cost_info}</Text>
                          </div>
                        )}
                        {(deliveryInfo.delivery_policy ||
                          deliveryInfo.return_policy) && (
                          <Collapse
                            ghost
                            size="small"
                            style={{
                              marginTop: "0.5rem",
                              background: "transparent",
                            }}
                          >
                            {deliveryInfo.delivery_policy && (
                              <Panel
                                header="Politique de livraison complète"
                                key="1"
                              >
                                <Text
                                  style={{
                                    fontSize: 13,
                                    whiteSpace: "pre-wrap",
                                  }}
                                >
                                  {deliveryInfo.delivery_policy}
                                </Text>
                              </Panel>
                            )}
                            {deliveryInfo.return_policy && (
                              <Panel header="Politique de retour" key="2">
                                <Text
                                  style={{
                                    fontSize: 13,
                                    whiteSpace: "pre-wrap",
                                  }}
                                >
                                  {deliveryInfo.return_policy}
                                </Text>
                              </Panel>
                            )}
                          </Collapse>
                        )}
                      </div>
                    }
                    type="info"
                    showIcon
                    style={{ marginBottom: "1.5rem" }}
                  />
                )}

              <Form
                form={customerForm}
                layout="vertical"
                onFinish={handleCustomerInfoSubmit}
              >
                <Row gutter={16}>
                  <Col span={12}>
                    <Form.Item
                      label="Nom complet"
                      name="customer_name"
                      rules={[{ required: true, message: "Le nom est requis" }]}
                    >
                      <Input placeholder="Votre nom complet" />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item
                      label="Téléphone"
                      name="customer_phone"
                      rules={[
                        { required: true, message: "Le téléphone est requis" },
                      ]}
                    >
                      <Input placeholder="+221 XX XXX XX XX" />
                    </Form.Item>
                  </Col>
                </Row>

                <Form.Item
                  label="Email (optionnel)"
                  name="customer_email"
                  rules={[{ type: "email", message: "Email invalide" }]}
                >
                  <Input placeholder="votre@email.com" />
                </Form.Item>

                {deliveryInfoLoading ? (
                  <Alert
                    message="Chargement des zones de livraison..."
                    type="info"
                    showIcon
                    style={{ marginBottom: "1rem" }}
                  />
                ) : deliveryInfoError ? (
                  <Alert
                    message="Zones de livraison indisponibles"
                    description="Le tarif de livraison n'a pas pu être vérifié. Réessayez avant de confirmer la commande."
                    type="error"
                    showIcon
                    action={
                      <Button size="small" onClick={fetchDeliveryInfo}>
                        Réessayer
                      </Button>
                    }
                    style={{ marginBottom: "1rem" }}
                  />
                ) : deliveryUnavailable ? (
                  <Alert
                    message="Livraison temporairement indisponible"
                    description="Aucune zone de livraison n'est active pour cette boutique. Contactez le marchand avant de commander."
                    type="warning"
                    showIcon
                    style={{ marginBottom: "1rem" }}
                  />
                ) : deliveryZones.length > 0 ? (
                  <Form.Item
                    label="Zone de livraison"
                    name="delivery_zone_id"
                    rules={[
                      {
                        required: true,
                        message: "Veuillez sélectionner votre zone de livraison",
                      },
                    ]}
                  >
                    <Select
                      size="large"
                      placeholder="Sélectionnez votre zone"
                      showSearch
                      optionFilterProp="label"
                      onChange={(value) =>
                        setSelectedDeliveryZoneId(Number(value))
                      }
                      options={deliveryZones.map((zone) => ({
                        value: zone.id,
                        label: `${zone.name} — ${formatCurrency(
                          zone.delivery_fee
                        )}`,
                      }))}
                    />
                  </Form.Item>
                ) : (
                  <Alert
                    message="Aucun frais de livraison configuré"
                    description="Aucun coût de livraison ne sera ajouté automatiquement à cette commande."
                    type="info"
                    showIcon
                    style={{ marginBottom: "1rem" }}
                  />
                )}

                <Form.Item
                  label="Adresse de livraison"
                  name="shipping_location"
                  rules={[
                    {
                      validator: () => {
                        if (shippingLocation?.street_address?.trim()) {
                          return Promise.resolve();
                        }
                        return Promise.reject(
                          new Error("Veuillez renseigner votre adresse de livraison")
                        );
                      },
                    },
                  ]}
                >
                  <LocationPicker onLocationSelect={handleShippingLocationSelect} />
                </Form.Item>

                <Form.Item>
                  <Button
                    type="primary"
                    htmlType="submit"
                    size="large"
                    loading={
                      paymentMethodsLoading || deliveryInfoLoading || loading
                    }
                    disabled={deliveryInfoError || deliveryUnavailable}
                  >
                    {hasOnlinePayment
                      ? "Choisir le mode de règlement"
                      : "Confirmer ma commande"}
                  </Button>
                </Form.Item>
              </Form>
            </Card>
          )}

          {hasOnlinePayment && currentStep === 1 && (
            <Card title="Mode de règlement">
              <Radio.Group
                value={paymentMethod}
                onChange={(e) => setPaymentMethod(e.target.value)}
                style={{ width: "100%" }}
              >
                <Space direction="vertical" style={{ width: "100%" }}>
                  {paymentMethodsAvailable.yengapay && (
                    <Radio value="yengapay">
                      <div>
                        <strong>Payer en ligne</strong>
                        <br />
                        <Text type="secondary">
                          Mobile Money (Orange, Moov, Sank, Coris, Telecel),
                          carte bancaire ou PayPal — paiement sécurisé via
                          YengaPay
                        </Text>
                      </div>
                    </Radio>
                  )}

                  {paymentMethodsAvailable.cod && (
                    <Radio value="cod">
                      <div>
                        <strong>Règlement à la livraison</strong>
                        <br />
                        <Text type="secondary">
                          Réglez en espèces lors de la réception de votre commande
                        </Text>
                      </div>
                    </Radio>
                  )}
                </Space>
              </Radio.Group>

              <Divider />

              <Space>
                <Button onClick={() => setCurrentStep(0)}>Retour</Button>
                <Button
                  type="primary"
                  size="large"
                  onClick={() => placeOrder(customerInfo, paymentMethod)}
                  loading={loading}
                >
                  {paymentMethod === "yengapay"
                    ? "Continuer vers le paiement sécurisé"
                    : "Confirmer ma commande"}
                </Button>
              </Space>
            </Card>
          )}
        </Col>

        {/* Récapitulatif à droite - seulement sur desktop */}
        {!isMobile && (
          <Col span={8}>
            <Card title="Récapitulatif de commande">
              <List
                dataSource={cartItems}
                renderItem={(item) => (
                  <List.Item style={{ padding: "0.5rem 0" }}>
                    <List.Item.Meta
                      title={
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                          }}
                        >
                          <Text>{item.product_name}</Text>
                          <Text>x{item.quantity}</Text>
                        </div>
                      }
                      description={
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                          }}
                        >
                          <Text type="secondary">
                            {new Intl.NumberFormat("fr-FR", {
                              style: "currency",
                              currency: "XOF",
                            }).format(item.unit_price)}{" "}
                            / unité
                          </Text>
                          <Text strong>
                            {new Intl.NumberFormat("fr-FR", {
                              style: "currency",
                              currency: "XOF",
                            }).format(item.item_total)}
                          </Text>
                        </div>
                      }
                    />
                  </List.Item>
                )}
              />

              <Divider />

              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Text>Sous-total</Text>
                <Text>{formatCurrency(total)}</Text>
              </div>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginTop: 8,
                }}
              >
                <Text>
                  Livraison
                  {selectedDeliveryZone ? ` · ${selectedDeliveryZone.name}` : ""}
                </Text>
                <Text>
                  {deliveryInfoError || deliveryUnavailable
                    ? "Indisponible"
                    : deliveryZones.length > 0 && !selectedDeliveryZone
                    ? "À sélectionner"
                    : formatCurrency(shippingAmount)}
                </Text>
              </div>

              {promotionControls}
              {appliedPromotion && (
                <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 8 }}>
                  <Text>Remise {appliedPromotion.code}</Text>
                  <Text type="success">−{formatCurrency(appliedPromotion.discount_amount)}</Text>
                </div>
              )}
              <Divider />

              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Title level={4}>Total à payer</Title>
                <Title level={4} style={{ color: "#52c41a" }}>
                  {formatCurrency(orderTotal)}
                </Title>
              </div>
            </Card>
          </Col>
        )}
      </Row>
    </div>
  );
};

export default Checkout;
